import React, { useState, useCallback, useMemo } from 'react';
import { Flex, xcss } from '@atlaskit/primitives';
import type { CategorySelectOption } from '@atlassian/jira-business-categories/src/common/types.tsx';
import { useCategoryCellStateController } from '@atlassian/jira-business-categories/src/controllers/category-cell-state-controller/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { CellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import {
	CREATE_CATEGORY_EXPERIENCE,
	UPDATE_CATEGORY_EXPERIENCE,
	DELETE_CATEGORY_EXPERIENCE,
} from '../../../common/constants.tsx';
import { CategoryCellContent } from './category-cell-content/index.tsx';
import DeleteCategoryModal from './delete-category-modal/index.tsx';
import { EditCategoryCell } from './edit-category-cell/index.tsx';
import messages from './messages.tsx';
import type { CategoryCellProps } from './types.tsx';

const CategoryCell = ({
	isEditable,
	fieldKey,
	fieldValue,
	issueKey,
	width,
	fieldType,
	issueTypeId,
	wrapperRef,
	fieldName,
	editMode = false,
	cellRef,
	issueType = '',
	inputValue,
	onInputChange,
}: CategoryCellProps) => {
	const [categoryToDeleteInModal, setCategoryToDeleteInModal] =
		useState<CategorySelectOption | null>(null);

	const {
		editFieldError,
		categoryState,
		createAndAssignNewCategory,
		checkCategoryNameAlreadyExists,
		updateCategory,
		deleteAndUnassignCategory,
		categoryOptions,
		assignCategoryToCell,
	} = useCategoryCellStateController({
		fieldValue,
		fieldType,
		issueKey,
		fieldKey,
		issueTypeId,
		createExperience: CREATE_CATEGORY_EXPERIENCE,
		updateExperience: UPDATE_CATEGORY_EXPERIENCE,
		deleteExperience: DELETE_CATEGORY_EXPERIENCE,
	});

	const project = useProject();

	const onDeleteCompleted = useCallback(() => {
		setCategoryToDeleteInModal(null);
	}, [setCategoryToDeleteInModal]);

	// we need to sort the options so that the selected category is at the top of the list
	// as the first option is hovered by default and will be selected if `Enter` is pressed
	// immediately after the select dropdown is shown
	const sortedCategories = useMemo<CategorySelectOption[]>(() => {
		if (categoryState == null) {
			return categoryOptions;
		}

		const selectedCategoryIndex = categoryOptions.findIndex(
			(option) => option.value === categoryState?.optionId,
		);

		if (selectedCategoryIndex === -1) {
			return categoryOptions;
		}

		const sortedCategoryOptions = [...categoryOptions];
		const selectedCategoryOption = sortedCategoryOptions[selectedCategoryIndex];

		sortedCategoryOptions.splice(selectedCategoryIndex, 1);
		sortedCategoryOptions.unshift(selectedCategoryOption);
		return sortedCategoryOptions;
	}, [categoryOptions, categoryState]);

	const triggerContent = useMemo(
		() => <CategoryCellContent isEditable={isEditable} categoryState={categoryState} />,
		[isEditable, categoryState],
	);

	const tmpError = project.isSimplified && editFieldError;

	const staticContent = triggerContent;

	const cellContent = (
		<>
			{editMode || categoryToDeleteInModal ? (
				<EditCategoryCell
					width={width}
					issueTypeId={issueTypeId}
					categoryOptions={sortedCategories}
					triggerContent={triggerContent}
					wrapperRef={wrapperRef}
					setCategoryToDeleteInModal={setCategoryToDeleteInModal}
					categoryState={categoryState}
					onCategoryNameAlreadyExists={checkCategoryNameAlreadyExists}
					onCategoryCreate={createAndAssignNewCategory}
					onCategoryCellValueChange={assignCategoryToCell}
					onCategoryUpdate={updateCategory}
					cellRef={cellRef}
					inputValue={inputValue}
					onInputChange={onInputChange}
				/>
			) : (
				staticContent
			)}
			{categoryToDeleteInModal && (
				<DeleteCategoryModal
					categoryToDelete={categoryToDeleteInModal}
					onDeleteCompleted={onDeleteCompleted}
					onCategoryDelete={deleteAndUnassignCategory}
				/>
			)}
			{tmpError && (
				<FixItErrorFlag
					error={editFieldError}
					title={messages.errorTitle}
					description={messages.errorMessage}
					issueType={issueType}
					issueTypeId={String(issueTypeId)}
					fieldId={fieldKey}
					fieldName={fieldName}
				/>
			)}
		</>
	);

	return (
		<CellWrapper
			isEditable={isEditable}
			hasPadding={false}
			testId="business-list.ui.list-view.category-cell.cell-container"
		>
			<Flex xcss={cellContentStyles}>{cellContent}</Flex>
		</CellWrapper>
	);
};

export default CategoryCell;

const cellContentStyles = xcss({ width: '100%', height: '100%' });
